import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  faSparkles,
  faSquare,
  faSquareCheck,
} from '@fortawesome/pro-duotone-svg-icons'

import Button from '../button/Button'
import Icon from '../icon/Icon'
import { useAuth } from '../../_hooks/use-auth'
import { getListingGoals, startGoalConversation } from '../../_data/goals'
import { getQuickPassSharePage } from '../../_data/share-page'
import { ListingGoal } from '../../_types/quickpass-goal'
import { InquireLauncherProps, InquireLauncherState } from './types'
import {
  Backdrop,
  BlockerTag,
  Card,
  CheckInput,
  CheckMark,
  CheckName,
  CheckValue,
  CharCount,
  ChecklistList,
  ChecklistRow,
  ChecklistTop,
  ErrorText,
  Footer,
  GoalCard,
  GoalIcon,
  GoalList,
  GoalMeta,
  GoalName,
  GoalText,
  LoadingText,
  MessageTextarea,
  ProofCallout,
  ReadyPill,
  ShareToggle,
  ShareToggleMark,
  ShareToggleSub,
  ShareToggleText,
  ShareToggleTitle,
  Subtitle,
  Title,
} from './styled'

const MIN_MESSAGE_LENGTH = 100

const initialiseState = (): InquireLauncherState => ({
  goals: [],
  isLoading: true,
  view: 'goals',
  selectedGoal: null,
  inputs: {},
  personalMessage: '',
  canShareProfile: false,
  includeShareLink: true,
  isSubmitting: false,
  error: '',
})

const countSatisfied = (goal: ListingGoal): number =>
  goal.conditions.filter(condition => condition.satisfied === true).length

/**
 * Inquiry launcher modal. Shows the property manager's configured goals
 * (Inquire, Schedule a Viewing, Join Waitlist, …) and, per goal, a
 * qualification checklist auto-filled from the renter's QuickPass profile +
 * verifications. Submitting kicks off a pre-filled Casa conversation and
 * routes the renter to their messages.
 *
 * @param {InquireLauncherProps} props
 * @returns {JSX.Element | null}
 */
const InquireLauncher = ({
  inventoryId,
  isOpen,
  onClose,
}: InquireLauncherProps): JSX.Element | null => {
  const { quickPassUser } = useAuth()
  const navigate = useNavigate()
  const [state, setState] = useState<InquireLauncherState>(initialiseState)

  useEffect(() => {
    if (isOpen === false || quickPassUser === null) {
      return
    }
    let isMounted = true
    setState(initialiseState())
    const load = async (): Promise<void> => {
      const [goals, sharePage] = await Promise.all([
        getListingGoals(quickPassUser.id, inventoryId),
        getQuickPassSharePage(quickPassUser.id),
      ])
      if (isMounted === false) {
        return
      }
      const canShareProfile =
        sharePage !== null &&
        sharePage.isActive === true &&
        sharePage.slug !== '' &&
        sharePage.shareToken !== ''
      setState(previous => ({
        ...previous,
        goals,
        canShareProfile,
        includeShareLink: canShareProfile,
        isLoading: false,
      }))
    }
    void load()
    return () => {
      isMounted = false
    }
  }, [isOpen, inventoryId, quickPassUser])

  const handleSelectGoal = useCallback((goal: ListingGoal): void => {
    setState(previous => ({
      ...previous,
      view: 'checklist',
      selectedGoal: goal,
      inputs: {},
      error: '',
    }))
  }, [])

  const handleBack = useCallback((): void => {
    setState(previous => ({
      ...previous,
      view: 'goals',
      selectedGoal: null,
      error: '',
    }))
  }, [])

  const doSubmit = useCallback(async (): Promise<void> => {
    const goal = state.selectedGoal
    if (goal === null || quickPassUser === null) {
      return
    }

    const answers = goal.conditions
      .map(condition => ({
        label: condition.name,
        value:
          condition.satisfied === true
            ? condition.value
            : (state.inputs[condition.id] ?? '').trim(),
      }))
      .filter(answer => answer.value !== '')

    setState(previous => ({ ...previous, isSubmitting: true, error: '' }))

    const result = await startGoalConversation(
      quickPassUser.id,
      inventoryId,
      goal.intentCode,
      goal.name,
      answers,
      goal.intentCode === 'general-inquiry' ? '' : state.personalMessage.trim(),
      state.canShareProfile === true && state.includeShareLink === true,
    )

    if (result === null) {
      setState(previous => ({
        ...previous,
        isSubmitting: false,
        error: 'Couldn’t start the conversation. Please try again.',
      }))
      return
    }

    onClose()
    navigate('/messages')
  }, [
    state.selectedGoal,
    state.inputs,
    state.personalMessage,
    state.canShareProfile,
    state.includeShareLink,
    quickPassUser,
    inventoryId,
    navigate,
    onClose,
  ])

  const handleContinueFromChecklist = useCallback((): void => {
    const goal = state.selectedGoal
    if (goal === null) {
      return
    }

    const missingBlocker = goal.conditions.find(
      condition =>
        condition.isBlocker === true &&
        condition.satisfied === false &&
        (state.inputs[condition.id] ?? '').trim() === '',
    )
    if (missingBlocker !== undefined) {
      setState(previous => ({
        ...previous,
        error: `${missingBlocker.name} is required to continue.`,
      }))
      return
    }

    if (goal.intentCode === 'general-inquiry') {
      void doSubmit()
      return
    }

    setState(previous => ({ ...previous, view: 'message', error: '' }))
  }, [state.selectedGoal, state.inputs, doSubmit])

  if (isOpen === false) {
    return null
  }

  return (
    <Backdrop onClick={onClose}>
      <Card onClick={event => event.stopPropagation()}>
        {state.isLoading === true ? (
          <LoadingText>Loading your options…</LoadingText>
        ) : state.view === 'goals' ? (
          <>
            <Title>How can the leasing team help?</Title>
            <Subtitle>
              Pick what you&apos;d like to do. We&apos;ll pre-fill everything we
              can from your QuickPass.
            </Subtitle>
            <GoalList>
              {state.goals.map(goal => {
                const total = goal.conditions.length
                const satisfied = countSatisfied(goal)
                return (
                  <GoalCard
                    key={goal.intentCode}
                    type="button"
                    onClick={() => handleSelectGoal(goal)}
                  >
                    <GoalIcon role="img" aria-label={goal.name}>
                      {goal.icon !== '' ? (
                        goal.icon
                      ) : (
                        <Icon icon={faSparkles} />
                      )}
                    </GoalIcon>
                    <GoalText>
                      <GoalName>{goal.name}</GoalName>
                      {goal.description !== '' ? (
                        <GoalMeta>{goal.description}</GoalMeta>
                      ) : null}
                    </GoalText>
                    {total > 0 ? (
                      <ReadyPill>
                        {satisfied}/{total} ready
                      </ReadyPill>
                    ) : null}
                  </GoalCard>
                )
              })}
            </GoalList>
            <Footer>
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </Footer>
          </>
        ) : state.view === 'checklist' && state.selectedGoal !== null ? (
          <>
            <Title>{state.selectedGoal.name}</Title>
            <Subtitle>
              {state.selectedGoal.conditions.length > 0
                ? 'Here’s what they need — we’ve checked off what your QuickPass already covers.'
                : 'No details needed — just start the conversation.'}
            </Subtitle>
            {state.selectedGoal.conditions.length > 0 ? (
              <ChecklistList>
                {state.selectedGoal.conditions.map(condition => (
                  <ChecklistRow key={condition.id}>
                    <ChecklistTop>
                      <CheckMark satisfied={condition.satisfied}>
                        <Icon
                          icon={
                            condition.satisfied === true
                              ? faSquareCheck
                              : faSquare
                          }
                        />
                      </CheckMark>
                      <CheckName>{condition.name}</CheckName>
                      {condition.isBlocker === true &&
                      condition.satisfied === false ? (
                        <BlockerTag>Required</BlockerTag>
                      ) : null}
                    </ChecklistTop>
                    {condition.satisfied === true ? (
                      <CheckValue>{condition.value}</CheckValue>
                    ) : (
                      <CheckInput
                        value={state.inputs[condition.id] ?? ''}
                        onChange={event => {
                          const next = event.target.value
                          setState(previous => ({
                            ...previous,
                            inputs: {
                              ...previous.inputs,
                              [condition.id]: next,
                            },
                          }))
                        }}
                      />
                    )}
                  </ChecklistRow>
                ))}
              </ChecklistList>
            ) : null}
            {state.error !== '' ? <ErrorText>{state.error}</ErrorText> : null}
            <Footer>
              <Button type="button" variant="ghost" onClick={handleBack}>
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                isLoading={state.isSubmitting}
                onClick={handleContinueFromChecklist}
              >
                {state.selectedGoal.intentCode === 'general-inquiry'
                  ? 'Start conversation'
                  : 'Continue'}
              </Button>
            </Footer>
          </>
        ) : state.view === 'message' && state.selectedGoal !== null ? (
          <>
            <Title>One last step</Title>
            <ProofCallout>
              A lot of renters tap inquire by accident or blast every listing at
              once — which buries the people who are genuinely interested. Write
              the leasing team a couple of real sentences about why this place
              works for you. It’s how you stand out.
            </ProofCallout>
            <MessageTextarea
              value={state.personalMessage}
              placeholder="Tell them a bit about yourself and why you’re interested in this place…"
              onChange={event => {
                const next = event.target.value
                setState(previous => ({ ...previous, personalMessage: next }))
              }}
            />
            <CharCount
              met={state.personalMessage.trim().length >= MIN_MESSAGE_LENGTH}
            >
              {state.personalMessage.trim().length} / {MIN_MESSAGE_LENGTH}
            </CharCount>
            {state.canShareProfile === true ? (
              <ShareToggle
                type="button"
                on={state.includeShareLink}
                onClick={() =>
                  setState(previous => ({
                    ...previous,
                    includeShareLink: previous.includeShareLink === false,
                  }))
                }
                aria-pressed={state.includeShareLink}
              >
                <ShareToggleMark>
                  <Icon
                    icon={
                      state.includeShareLink === true ? faSquareCheck : faSquare
                    }
                  />
                </ShareToggleMark>
                <ShareToggleText>
                  <ShareToggleTitle>
                    Include my QuickPass profile link
                  </ShareToggleTitle>
                  <ShareToggleSub>
                    Lets them open your full renter pass — verifications and
                    all.
                  </ShareToggleSub>
                </ShareToggleText>
              </ShareToggle>
            ) : null}
            {state.error !== '' ? <ErrorText>{state.error}</ErrorText> : null}
            <Footer>
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  setState(previous => ({
                    ...previous,
                    view: 'checklist',
                    error: '',
                  }))
                }
              >
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                isLoading={state.isSubmitting}
                disabled={
                  state.personalMessage.trim().length < MIN_MESSAGE_LENGTH
                }
                onClick={() => {
                  void doSubmit()
                }}
              >
                Send to leasing team
              </Button>
            </Footer>
          </>
        ) : null}
      </Card>
    </Backdrop>
  )
}

export default InquireLauncher
